import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE, authHeaders } from '../utils/api';
import './Dashboard.css';

const Dashboard = () => {
  const [stats, setStats] = useState({
    news: { total: 0, pending: 0, approved: 0, rejected: 0 },
    admissions: { total: 0, pending: 0, approved: 0, rejected: 0 },
    examinations: { total: 0, pending: 0, approved: 0, rejected: 0 },
    faculty: { total: 0, pending: 0, approved: 0, rejected: 0 },
    recruiters: { total: 0, pending: 0, approved: 0, rejected: 0 },
    enquiries: { total: 0, pending: 0, approved: 0, rejected: 0 },
  });

  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/stats/dashboard`, {
        headers: authHeaders(),
      });

      if (!res.ok) {
        throw new Error('Failed to fetch stats');
      }

      const data = await res.json();
      if (data.stats) {
        setStats({
          news: data.stats.news || { total: 0, pending: 0, approved: 0, rejected: 0 },
          admissions: data.stats.admissions || { total: 0, pending: 0, approved: 0, rejected: 0 },
          examinations: data.stats.examinations || { total: 0, pending: 0, approved: 0, rejected: 0 },
          faculty: data.stats.faculty || { total: 0, pending: 0, approved: 0, rejected: 0 },
          recruiters: data.stats.recruiters || { total: 0, pending: 0, approved: 0, rejected: 0 },
          enquiries: data.stats.enquiries || { total: 0, pending: 0, approved: 0, rejected: 0 },
        });
      }
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      // Keep default values of 0 on error
    }

    // Recent activity can be fetched from change logs later
    setRecentActivity([
      { id: 1, type: 'news', action: 'Created', title: 'New Academic News', time: '2 hours ago', user: 'Admin' },
      { id: 2, type: 'admission', action: 'Updated', title: 'Admission FAQ', time: '5 hours ago', user: 'Coordinator' },
      { id: 3, type: 'exam', action: 'Created', title: 'Exam Timetable', time: '1 day ago', user: 'Admin' },
      { id: 4, type: 'faculty', action: 'Updated', title: 'Faculty Profile', time: '2 days ago', user: 'Coordinator' },
    ]);
  };

  const statCards = [
    { label: 'News', data: stats.news, icon: '📰', color: '#3b82f6', link: '/admin/media/latest-news' },
    { label: 'Admissions', data: stats.admissions, icon: '🎓', color: '#10b981', link: '/admin/admissions/faqs' },
    { label: 'Examinations', data: stats.examinations, icon: '📝', color: '#f59e0b', link: '/admin/examinations/notices-circulars' },
    { label: 'Faculty', data: stats.faculty, icon: '🏫', color: '#8b5cf6', link: '/admin/academics/faculty-information' },
    { label: 'Recruiters', data: stats.recruiters, icon: '💼', color: '#ec4899', link: '/admin/placement/top-recruiters' },
    { label: 'Enquiries', data: stats.enquiries, icon: '📧', color: '#06b6d4', link: '/admin/applications/enquire-now' },
  ];

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p className="dashboard-subtitle">Welcome back! Here's what's happening today.</p>
      </div>

      {/* Stats Grid */}
      <div className="dashboard-stats">
        {statCards.map((stat, index) => (
          <Link key={index} to={stat.link} className="stat-card">
            <div className="stat-card-icon" style={{ background: `${stat.color}15`, color: stat.color }}>
              <span>{stat.icon}</span>
            </div>
            <div className="stat-card-content">
              <div className="stat-card-value">{stat.data.total}</div>
              <div className="stat-card-label">{stat.label}</div>
              <div className="stat-card-description">Total Entries</div>
            </div>
            <div className="stat-card-arrow">→</div>
          </Link>
        ))}
      </div>

      <div className="dashboard-grid">
        {/* Recent Activity */}
        <div className="dashboard-card">
          <div className="dashboard-card-header">
            <h2>Recent Activity</h2>
            <Link to="/admin/login-logs" className="view-all-link">View All</Link>
          </div>
          <div className="activity-list">
            {recentActivity.length > 0 ? (
              recentActivity.map((activity) => (
                <div key={activity.id} className="activity-item">
                  <div className="activity-icon">
                    {activity.type === 'news' && '📰'}
                    {activity.type === 'admission' && '🎓'}
                    {activity.type === 'exam' && '📝'}
                    {activity.type === 'faculty' && '🏫'}
                  </div>
                  <div className="activity-content">
                    <div className="activity-title">
                      <span className="activity-action">{activity.action}</span> {activity.title}
                    </div>
                    <div className="activity-meta">
                      {activity.user} • {activity.time}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="activity-empty">No recent activity</div>
            )}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="dashboard-card">
        <div className="dashboard-card-header">
          <h2>System Status</h2>
        </div>
        <div className="system-status">
          <div className="status-item">
            <div className="status-indicator status-online"></div>
            <div className="status-info">
              <div className="status-label">Database</div>
              <div className="status-value">Connected</div>
            </div>
          </div>
          <div className="status-item">
            <div className="status-indicator status-online"></div>
            <div className="status-info">
              <div className="status-label">API Server</div>
              <div className="status-value">Running</div>
            </div>
          </div>
          <div className="status-item">
            <div className="status-indicator status-online"></div>
            <div className="status-info">
              <div className="status-label">Last Backup</div>
              <div className="status-value">2 hours ago</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;